import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star, Copy, Send, Loader2, Mail, MessageSquare } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";

function History({ items }) {
  if (!items?.length) return <p className="text-xs text-slate-400" data-testid="review-req-history-empty">No review requests sent yet.</p>;
  return (
    <ul className="space-y-1 max-h-32 overflow-y-auto" data-testid="review-req-history">
      {items.map((h) => (
        <li key={h.id} className="text-xs text-slate-600 flex justify-between gap-2">
          <span>{h.channel === "copied" ? "Link copied" : <><span className="capitalize">{h.channel}</span> to {h.to || "-"}</>}</span>
          <span className={h.status === "failed" ? "text-rose-600" : "text-slate-400"}>{h.status === "failed" ? "Failed · " : ""}{h.by} · {formatDateTime(h.at)}</span>
        </li>
      ))}
    </ul>
  );
}

export function ReviewRequestButton({ legacyId, className, open: openProp, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = controlled ? onOpenChange : setOpenState;
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/jobs/${legacyId}/review-request`).then((r) => setInfo(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load review request"));
  const openDlg = () => { setOpen(true); load(); };
  useEffect(() => { if (controlled && openProp) { load(); } }, [controlled, openProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (channel) => {
    setBusy(channel);
    try {
      const r = await api.post(`/jobs/${legacyId}/review-request`, { channel });
      if (channel !== "copied") toast.success(`Review request sent by ${r.data.log.channel} to ${r.data.log.to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send review request"); load(); }
    finally { setBusy(null); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(info.message); toast.success("Review message copied"); } catch { toast.error("Copy failed"); }
    send("copied");
  };

  const blocked = !!info?.blocked_until;
  const canSms = info?.mobile && info?.sms_live;
  return (
    <>
      {!controlled && <button className={className} onClick={openDlg} data-testid="job-ql-request-review"><Star className="w-3.5 h-3.5" /> Request a review</button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="review-req-dialog">
          <DialogHeader>
            <DialogTitle>Request a Trustpilot review</DialogTitle>
            <DialogDescription>{info?.customer_name || "Customer"} · {info?.mobile || "no mobile"} · {info?.email || "no email"}</DialogDescription>
          </DialogHeader>
          {!info ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : !info.link ? (
            <p className="text-sm text-rose-600" data-testid="review-req-no-link">No Trustpilot review link is set for this company.</p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 break-words" data-testid="review-req-message">{info.message}</p>
              {blocked && <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2" data-testid="review-req-blocked">Already sent on {formatDateTime(info.last_sent.at)} by {info.last_sent.channel}. You can send again after {formatDateTime(info.blocked_until)}.</p>}
              {!info.sms_live && info.mobile && <p className="text-xs text-slate-500" data-testid="review-req-sms-off">SMS sending is switched off, so this will go by email.</p>}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => send("auto")} disabled={blocked || !!busy || (!canSms && !info.email)} data-testid="review-req-send">
                  {busy === "auto" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send {canSms ? <><MessageSquare className="w-3.5 h-3.5 mx-1" />SMS</> : <><Mail className="w-3.5 h-3.5 mx-1" />email</>}
                </Button>
                {canSms && info.email && <Button size="sm" variant="outline" onClick={() => send("email")} disabled={blocked || !!busy} data-testid="review-req-send-email"><Mail className="w-4 h-4 mr-1" /> Email instead</Button>}
                <Button size="sm" variant="outline" onClick={copy} disabled={!!busy} data-testid="review-req-copy"><Copy className="w-4 h-4 mr-1" /> Copy message</Button>
              </div>
              <div className="flex items-center gap-4">
                <img src={info.qr} alt="Trustpilot review QR code" className="w-28 h-28 border border-slate-200 rounded-md" data-testid="review-req-qr" />
                <div className="text-xs text-slate-500 break-all">Customer can scan this to review:<br /><a href={info.link} target="_blank" rel="noreferrer" className="text-indigo-600" data-testid="review-req-link">{info.link}</a></div>
              </div>
              <div><h4 className="text-xs font-semibold text-slate-700 mb-1">Send log</h4><History items={info.history} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
